import type { InitModule } from '~/types'

import { watch } from 'vue'
import { whenever } from '@vueuse/core'
import { useQueue } from '~/composables/audio/queue'
import { usePlayer } from '~/composables/audio/player'
import { isWebAudioRequested, AUDIO_CONTEXT } from '~/composables/audio/audio-api'

export const install: InitModule = ({ store }) => {
  const { currentTrack, playNext, playPrevious } = useQueue()
  const { isPlaying, seekBy, seekTo, pauseReason, PauseReason, currentTime, duration } = usePlayer()

  if ('mediaSession' in navigator) {
    // Basic Controls
    navigator.mediaSession.setActionHandler('play', async () => {
      // Only resume the context if the user has Milkdrop enabled
      if (isWebAudioRequested.value && AUDIO_CONTEXT.state === 'suspended') {
        await AUDIO_CONTEXT.resume()
      }
      isPlaying.value = true
    })
    navigator.mediaSession.setActionHandler('pause', () => {
      isPlaying.value = false
      pauseReason.value = PauseReason.MediaSession
    })

    // Seeking logic
    navigator.mediaSession.setActionHandler('seekforward', () => seekBy(5))
    navigator.mediaSession.setActionHandler('seekbackward', () => seekBy(-5))

    // Enables scrubbing on the lockscreen progress bar
    navigator.mediaSession.setActionHandler('seekto', async (details) => {
      if (details.seekTime !== undefined) {
        await seekTo(details.seekTime) // Ensure seekTo is awaited if it's async
        updatePositionState()
      }
    })

    navigator.mediaSession.setActionHandler('nexttrack', () => playNext())
    navigator.mediaSession.setActionHandler('previoustrack', () => playPrevious())

    // Sync Playback State - Essential for iOS background survival
    watch(isPlaying, (playing: boolean) => {
      navigator.mediaSession.playbackState = playing ? 'playing' : 'paused'
    }, { immediate: true })

    // Allow scrubbing in iOS
    watch([isPlaying, duration], updatePositionState, { immediate: true })

    watch(currentTime, () => {
      if (isPlaying.value) {
        updatePositionState()
      }
    })

    function updatePositionState() {
      if ('mediaSession' in navigator && 'setPositionState' in navigator.mediaSession) {
        const d = duration.value || 0
        const p = currentTime.value || 0
        if (d > 0 && p <= d) {
          navigator.mediaSession.setPositionState({
            duration: d,
            playbackRate: 1,
            position: p
          })
        }
      }
    }

    // Update Metadata
    whenever(currentTrack, (track) => {
      if (!track) {
        navigator.mediaSession.metadata = null
        return
      }

      // Convert relative to absolute URL (iOS Requirement)
      const absoluteCoverUrl = store.getters['instance/absoluteUrl'](track.coverUrl)
      const artistName = track.artistCredit?.map(ac => ac.artist.name).join(', ') || 'Unknown Artist'

      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: track.title,
        artist: artistName,
        album: track.albumTitle || '',
        artwork: [
          { src: absoluteCoverUrl, sizes: '512x512', type: 'image/jpeg' },
          { src: absoluteCoverUrl, sizes: '1024x1024', type: 'image/jpeg' }
        ]
      })
    }, { immediate: true })
  }
}
